import { useState, useEffect, useRef } from "react";
import { FileText, Save, ChevronDown, ChevronRight, Plus, Trash2, GripVertical, Image, Upload, X, Check } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

import { API } from "../../config/api";

const PAGE_SLUGS = [
  { slug: "home", name: "Home Page" },
  { slug: "about", name: "About Page" },
  { slug: "contact", name: "Contact Page" },
  { slug: "sponsors", name: "Sponsors Page" },
  { slug: "charity-food-drive", name: "Food Drive" },
  { slug: "charity-blood-donation", name: "Blood Donation" }
];

export default function AdminSitePages() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  
  // Image picker state
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState(null); // {sectionIndex, field}
  const [imagePickerTab, setImagePickerTab] = useState("upload");
  const [galleryImages, setGalleryImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchPages();
  }, []);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
    "Content-Type": "application/json"
  });

  const fetchPages = async () => {
    try {
      const response = await fetch(`${API}/admin/site-pages`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error("Error fetching pages:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGalleryImages = async () => {
    try {
      const response = await fetch(`${API}/admin/gallery`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGalleryImages(data.filter(item => item.media_type === "image" || !item.media_type));
      }
    } catch (error) {
      console.error("Error fetching gallery:", error);
    }
  };

  const loadPage = async (slug) => {
    setSelectedPage(slug);
    try {
      const response = await fetch(`${API}/admin/site-pages/${slug}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPageData(data);
        const expanded = {};
        (data.sections || []).forEach((_, idx) => {
          expanded[idx] = true;
        });
        setExpandedSections(expanded);
      }
    } catch (error) {
      console.error("Error loading page:", error);
      toast.error("Failed to load page");
    }
  };

  const savePage = async () => {
    if (!selectedPage || !pageData) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/site-pages/${selectedPage}`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          title: pageData.title,
          sections: pageData.sections
        })
      });

      if (response.ok) {
        toast.success("Page saved successfully!");
        fetchPages();
      } else {
        toast.error("Failed to save page");
      }
    } catch (error) {
      console.error("Error saving page:", error);
      toast.error("Error saving page");
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (index, field, value) => {
    setPageData(prev => {
      const newSections = [...prev.sections];
      newSections[index] = { ...newSections[index], [field]: value };
      return { ...prev, sections: newSections };
    });
  };

  const updateSectionItem = (sectionIndex, itemIndex, field, value) => {
    setPageData(prev => {
      const newSections = [...prev.sections];
      const newItems = [...(newSections[sectionIndex].items || [])];
      newItems[itemIndex] = { ...newItems[itemIndex], [field]: value };
      newSections[sectionIndex] = { ...newSections[sectionIndex], items: newItems };
      return { ...prev, sections: newSections };
    });
  };

  const addSection = () => {
    setPageData(prev => ({
      ...prev,
      sections: [
        ...(prev.sections || []),
        {
          type: "text",
          heading: "New Section",
          subheading: "",
          body: "",
          image_url: "",
          cta_text: "",
          cta_link: "",
          items: [],
          order: (prev.sections || []).length
        }
      ]
    }));
  };

  const removeSection = (index) => {
    setPageData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }));
  };

  const toggleSection = (index) => {
    setExpandedSections(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Image Picker Functions
  const openImagePicker = (sectionIndex, field = "image_url") => {
    setImagePickerTarget({ sectionIndex, field });
    setImagePickerTab("upload");
    setImagePickerOpen(true);
    fetchGalleryImages();
  };

  const closeImagePicker = () => {
    setImagePickerOpen(false);
    setImagePickerTarget(null);
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await fetch(`${API}/admin/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        if (imagePickerTarget) {
          updateSection(imagePickerTarget.sectionIndex, imagePickerTarget.field, data.url);
          toast.success("Image uploaded!");
          closeImagePicker();
        }
      } else {
        toast.error("Failed to upload image");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Error uploading image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const selectGalleryImage = (url) => {
    if (imagePickerTarget) {
      updateSection(imagePickerTarget.sectionIndex, imagePickerTarget.field, url);
      toast.success("Image selected!");
      closeImagePicker();
    }
  };

  const renderSectionEditor = (section, index) => {
    const isExpanded = expandedSections[index];
    
    return (
      <div key={index} className="border border-kerala-gold/20 rounded-lg bg-white mb-4">
        {/* Section Header */}
        <div 
          className="flex items-center justify-between p-4 cursor-pointer hover:bg-kerala-cream/50"
          onClick={() => toggleSection(index)}
        >
          <div className="flex items-center gap-3">
            <GripVertical className="w-4 h-4 text-gray-400" />
            {isExpanded ? (
              <ChevronDown className="w-4 h-4 text-kerala-green" />
            ) : (
              <ChevronRight className="w-4 h-4 text-kerala-green" />
            )}
            <span className="font-medium text-kerala-green">
              Section {index + 1}: {section.type || "Unknown"}
            </span>
            <span className="text-sm text-gray-500">
              {section.heading || "(No heading)"}
            </span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              removeSection(index);
            }}
            className="text-red-500 hover:text-red-700 hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>

        {/* Section Content */}
        {isExpanded && (
          <div className="p-4 border-t border-kerala-gold/10 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Section Type</Label>
                <Input
                  value={section.type || ""}
                  onChange={(e) => updateSection(index, "type", e.target.value)}
                  placeholder="hero, text, values, etc."
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Order</Label>
                <Input
                  type="number"
                  value={section.order || 0}
                  onChange={(e) => updateSection(index, "order", parseInt(e.target.value) || 0)}
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label>Heading</Label>
              <Input
                value={section.heading || ""}
                onChange={(e) => updateSection(index, "heading", e.target.value)}
                placeholder="Section heading"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Subheading</Label>
              <Input
                value={section.subheading || ""}
                onChange={(e) => updateSection(index, "subheading", e.target.value)}
                placeholder="Optional subheading"
                className="mt-1"
              />
            </div>

            <div>
              <Label>Body Text</Label>
              <Textarea
                value={section.body || ""}
                onChange={(e) => updateSection(index, "body", e.target.value)}
                placeholder="Main content text"
                rows={4}
                className="mt-1"
              />
            </div>

            {/* Image URL with Upload Button */}
            <div>
              <Label>Image</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={section.image_url || ""}
                  onChange={(e) => updateSection(index, "image_url", e.target.value)}
                  placeholder="/api/uploads/... or https://..."
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => openImagePicker(index, "image_url")}
                  className="shrink-0"
                >
                  <Image className="w-4 h-4 mr-1" />
                  Choose
                </Button>
              </div>
              {section.image_url && (
                <div className="mt-2 relative inline-block">
                  <img 
                    src={section.image_url.startsWith("http") ? section.image_url : section.image_url} 
                    alt="Preview" 
                    className="h-20 rounded border border-kerala-gold/20 object-cover"
                    onError={(e) => e.target.style.display = "none"}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>CTA Text</Label>
                <Input
                  value={section.cta_text || ""}
                  onChange={(e) => updateSection(index, "cta_text", e.target.value)}
                  placeholder="Button text"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>CTA Link</Label>
                <Input
                  value={section.cta_link || ""}
                  onChange={(e) => updateSection(index, "cta_link", e.target.value)}
                  placeholder="/vishu-2026"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Items Editor */}
            {section.items && section.items.length > 0 && (
              <div className="mt-4 p-4 bg-kerala-cream/50 rounded-lg">
                <Label className="text-kerala-green font-semibold">Items ({section.items.length})</Label>
                <div className="space-y-3 mt-2">
                  {section.items.map((item, itemIdx) => (
                    <div key={itemIdx} className="p-3 bg-white rounded border border-kerala-gold/10">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-kerala-green">Item {itemIdx + 1}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {Object.entries(item).map(([key, value]) => (
                          <div key={key}>
                            <Label className="text-xs">{key}</Label>
                            {typeof value === "string" && value.length > 100 ? (
                              <Textarea
                                value={value}
                                onChange={(e) => updateSectionItem(index, itemIdx, key, e.target.value)}
                                rows={2}
                                className="mt-0.5 text-sm"
                              />
                            ) : typeof value === "string" ? (
                              <Input
                                value={value}
                                onChange={(e) => updateSectionItem(index, itemIdx, key, e.target.value)}
                                className="mt-0.5 text-sm"
                              />
                            ) : Array.isArray(value) ? (
                              <Textarea
                                value={value.join("\n")}
                                onChange={(e) => updateSectionItem(index, itemIdx, key, e.target.value.split("\n"))}
                                rows={3}
                                className="mt-0.5 text-sm"
                                placeholder="One item per line"
                              />
                            ) : null}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-site-pages">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="font-heading text-3xl text-kerala-green">Site Pages</h1>
          <p className="text-gray-600">Edit content for all public pages</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Page List */}
        <div className="lg:col-span-1 bg-white p-4 rounded-lg border border-kerala-gold/20">
          <h2 className="font-heading text-lg text-kerala-green mb-4">Pages</h2>
          <div className="space-y-2">
            {PAGE_SLUGS.map((page) => {
              const exists = pages.find(p => p.slug === page.slug);
              return (
                <button
                  key={page.slug}
                  onClick={() => loadPage(page.slug)}
                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                    selectedPage === page.slug
                      ? "bg-kerala-green text-white"
                      : "hover:bg-kerala-cream text-gray-700"
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  <span className="flex-1">{page.name}</span>
                  {exists && (
                    <span className="w-2 h-2 rounded-full bg-green-500" title="Has content" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {!selectedPage ? (
            <div className="bg-white p-8 rounded-lg border border-kerala-gold/20 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Select a page from the list to edit</p>
            </div>
          ) : !pageData ? (
            <div className="bg-white p-8 rounded-lg border border-kerala-gold/20 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mx-auto" />
              <p className="text-gray-500 mt-4">Loading page...</p>
            </div>
          ) : (
            <div className="bg-white p-6 rounded-lg border border-kerala-gold/20">
              {/* Page Title */}
              <div className="flex justify-between items-start mb-6">
                <div className="flex-1 mr-4">
                  <Label>Page Title</Label>
                  <Input
                    value={pageData.title || ""}
                    onChange={(e) => setPageData({ ...pageData, title: e.target.value })}
                    className="mt-1 text-lg font-semibold"
                    placeholder="Page Title"
                  />
                </div>
                <Button
                  onClick={savePage}
                  disabled={saving}
                  className="bg-kerala-green hover:bg-kerala-green/90"
                >
                  {saving ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                      Saving...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Save className="w-4 h-4" />
                      Save Page
                    </span>
                  )}
                </Button>
              </div>

              {/* Sections */}
              <div className="mb-4">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="font-heading text-xl text-kerala-green">
                    Sections ({(pageData.sections || []).length})
                  </h2>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={addSection}
                    className="border-kerala-green text-kerala-green hover:bg-kerala-green/5"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Section
                  </Button>
                </div>

                {(pageData.sections || []).length === 0 ? (
                  <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                    No sections yet. Click "Add Section" to create one.
                  </div>
                ) : (
                  pageData.sections.map((section, idx) => renderSectionEditor(section, idx))
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Picker Modal */}
      {imagePickerOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-heading text-xl text-kerala-green">Choose Image</h3>
              <Button variant="ghost" size="icon" onClick={closeImagePicker}>
                <X className="w-5 h-5" />
              </Button>
            </div>

            {/* Tabs */}
            <div className="flex border-b">
              <button
                onClick={() => setImagePickerTab("upload")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  imagePickerTab === "upload"
                    ? "border-b-2 border-kerala-green text-kerala-green"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                Upload New
              </button>
              <button
                onClick={() => setImagePickerTab("gallery")}
                className={`flex-1 px-4 py-3 text-sm font-medium ${
                  imagePickerTab === "gallery"
                    ? "border-b-2 border-kerala-green text-kerala-green"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <Image className="w-4 h-4 inline mr-2" />
                From Gallery
              </button>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-auto p-4">
              {imagePickerTab === "upload" ? (
                <div className="text-center py-8">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="image-upload"
                  />
                  <label
                    htmlFor="image-upload"
                    className={`inline-flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      uploading 
                        ? "border-kerala-green/50 bg-kerala-cream/50" 
                        : "border-gray-300 hover:border-kerala-green hover:bg-kerala-cream/30"
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin w-8 h-8 border-4 border-kerala-green border-t-transparent rounded-full mb-2" />
                        <span className="text-gray-600">Uploading...</span>
                      </>
                    ) : (
                      <>
                        <Upload className="w-10 h-10 text-gray-400 mb-2" />
                        <span className="text-gray-600">Click to upload image</span>
                        <span className="text-xs text-gray-400 mt-1">PNG, JPG, WEBP up to 10MB</span>
                      </>
                    )}
                  </label>
                </div>
              ) : (
                <div>
                  {galleryImages.length === 0 ? (
                    <p className="text-center text-gray-500 py-8">No images in gallery</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {galleryImages.map((img) => (
                        <div
                          key={img.id}
                          onClick={() => selectGalleryImage(img.url)}
                          className="relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-kerala-green transition-colors"
                        >
                          <img
                            src={img.url?.startsWith("http") ? img.url : img.url}
                            alt={img.title}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                            <Check className="w-8 h-8 text-white" />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
